import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill from '../components/ui/Pill';
import { EmptyState, SkeletonRows, ErrorNotice } from '../components/ui/States';
import { fmt, dateShort, orDash } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import type {
  CreatePaymentRequest,
  DataResponse,
  PaymentEntryDetail,
  PaymentList,
  PaymentListQuery,
  PaymentStatusRequest,
  PaymentStatusResult,
  PaymentSummary,
  PaymentSummaryQuery,
  UpdatePaymentRequest,
} from '../contracts';
import {
  PAYMENT_KINDS,
  PAYMENT_STATUSES,
  PAYMENT_STATUS_LABEL,
  PAYMENT_TONE,
  money,
  toCents,
  personName,
  errorMessage,
  inputClass,
  selectClass,
} from '../lib/programs';

const GUARD_NOTE =
  'Never enter banking details, card numbers, SINs or receipts. York finance collects those through its own process; the ledger records status only.';

function NewEntry({ fiscalYear, onClose, onSaved }: { fiscalYear?: string; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    kind: 'honorarium',
    email: '',
    amount: '',
    status: 'owed',
    fiscalYear: fiscalYear ?? '',
    note: '',
    yorkReference: '',
  });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const cents = toCents(form.amount);
  const save = useMutation(
    async () =>
      api.post<DataResponse<PaymentEntryDetail>>('/payments', {
        kind: form.kind as CreatePaymentRequest['kind'],
        email: form.email,
        amountCents: cents,
        status: form.status as CreatePaymentRequest['status'],
        fiscalYear: form.fiscalYear || undefined,
        note: form.note || undefined,
        yorkReference: form.yorkReference || undefined,
      } satisfies CreatePaymentRequest),
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title="New ledger entry"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!form.email.trim() || Number.isNaN(cents) || save.isLoading}
            onClick={() => save.mutate()}
          >
            {save.isLoading ? 'Saving…' : 'Add entry'}
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">Email of a member or a contact</label>
          <input className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} />
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Kind</label>
          <select className={inputClass} value={form.kind} onChange={(e) => set('kind', e.target.value)}>
            {Object.entries(PAYMENT_KINDS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Amount (CAD, optional)</label>
          <input
            className={inputClass}
            placeholder="250.00"
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Status</label>
          <select className={inputClass} value={form.status} onChange={(e) => set('status', e.target.value)}>
            {PAYMENT_STATUSES.filter((s) => !['cleared', 'cancelled'].includes(s)).map((s) => (
              <option key={s} value={s}>
                {PAYMENT_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Fiscal year</label>
          <input
            className={inputClass}
            placeholder="2026-27"
            value={form.fiscalYear}
            onChange={(e) => set('fiscalYear', e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">York reference (eReports / Concur id, optional)</label>
          <input
            className={inputClass}
            value={form.yorkReference}
            onChange={(e) => set('yorkReference', e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <label className="block text-small text-ink-2 mb-1">Note (optional)</label>
          <textarea
            className={`${inputClass} h-[64px]`}
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
          />
          <p className="text-small text-ink-2 mt-1">{GUARD_NOTE}</p>
        </div>
      </div>
      {Number.isNaN(cents) ? (
        <div className="mt-3">
          <ErrorNotice>The amount looks like 250 or 250.00.</ErrorNotice>
        </div>
      ) : null}
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not add the entry.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function EntryDrawer({ id, onClose, onSaved }: { id: string; onClose: () => void; onSaved: () => void }) {
  const queryClient = useQueryClient();
  const entry = useQuery<PaymentEntryDetail>(
    ['payment', id],
    async () => (await api.get<DataResponse<PaymentEntryDetail>>(`/payments/${id}`)).data.data,
  );
  const [edit, setEdit] = useState<{ amount: string; note: string; yorkReference: string } | null>(null);
  const [next, setNext] = useState('');
  const [note, setNote] = useState('');
  const done = () => {
    queryClient.invalidateQueries(['payment', id]);
    onSaved();
  };
  const e = entry.data;
  const amountEditable = Boolean(e && !['paid', 'cleared', 'cancelled'].includes(e.status));
  const editCents = edit && edit.amount !== '' ? toCents(edit.amount) : null;
  const save = useMutation(
    async () =>
      api.patch<DataResponse<PaymentEntryDetail>>(`/payments/${id}`, {
        ...(amountEditable && editCents !== null && editCents !== e?.amountCents ? { amountCents: editCents } : {}),
        note: edit?.note ?? '',
        yorkReference: edit?.yorkReference ?? '',
      } satisfies UpdatePaymentRequest),
    {
      onSuccess: () => {
        setEdit(null);
        done();
      },
    },
  );
  const move = useMutation(
    async () =>
      api.post<DataResponse<PaymentStatusResult>>('/payments/status', {
        ids: [id],
        status: next as PaymentStatusRequest['status'],
        note: note || undefined,
      } satisfies PaymentStatusRequest),
    {
      onSuccess: () => {
        setNext('');
        setNote('');
        done();
      },
    },
  );
  return (
    <Modal title={e ? personName(e.person) : 'Ledger entry'} onClose={onClose} width={620}>
      {entry.isLoading ? (
        <SkeletonRows rows={4} />
      ) : entry.error ? (
        <ErrorNotice>Could not load the entry.</ErrorNotice>
      ) : e ? (
        <>
          <div className="grid grid-cols-2 gap-y-2 text-table">
            <div className="text-ink-2">Kind</div>
            <div>{PAYMENT_KINDS[e.kind] ?? e.kind}</div>
            <div className="text-ink-2">Amount</div>
            <div>{money(e.amountCents)}</div>
            <div className="text-ink-2">Status</div>
            <div>
              <Pill tone={PAYMENT_TONE[e.status]}>{PAYMENT_STATUS_LABEL[e.status]}</Pill>
            </div>
            <div className="text-ink-2">Activity</div>
            <div>
              {e.activity.cycle
                ? `${e.activity.cycle.program ?? ''} ${e.activity.cycle.label}`
                : e.activity.event
                  ? e.activity.event.title
                  : '—'}
            </div>
            <div className="text-ink-2">Fiscal year</div>
            <div>{e.fiscalYear}</div>
            <div className="text-ink-2">York reference</div>
            <div>{orDash(e.yorkReference)}</div>
            <div className="text-ink-2">Note</div>
            <div>{orDash(e.note)}</div>
            {e.person.type === 'member' && e.person.id ? (
              <>
                <div className="text-ink-2">Member</div>
                <Link className="underline" to={`/analytics/members/${e.person.id}`}>
                  Member 360
                </Link>
              </>
            ) : null}
            {e.person.type === 'detached' ? (
              <div className="col-span-2 text-ink-2 text-small">
                The member record was deleted; the entry keeps the name it had.
              </div>
            ) : null}
          </div>
          {edit ? (
            <div className="mt-5 space-y-3">
              {amountEditable ? (
                <input
                  className={inputClass}
                  placeholder="Amount (CAD)"
                  value={edit.amount}
                  onChange={(ev) => setEdit({ ...edit, amount: ev.target.value })}
                />
              ) : null}
              <input
                className={inputClass}
                placeholder="York reference"
                value={edit.yorkReference}
                onChange={(ev) => setEdit({ ...edit, yorkReference: ev.target.value })}
              />
              <textarea
                className={`${inputClass} h-[56px]`}
                placeholder="Note"
                value={edit.note}
                onChange={(ev) => setEdit({ ...edit, note: ev.target.value })}
              />
              <p className="text-small text-ink-2">{GUARD_NOTE}</p>
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={save.isLoading || Number.isNaN(editCents)}
                  onClick={() => save.mutate()}
                >
                  Save
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEdit(null)}>
                  Cancel
                </Button>
              </div>
              {save.error ? <ErrorNotice>{errorMessage(save.error, 'Could not save.')}</ErrorNotice> : null}
            </div>
          ) : (
            <div className="mt-4">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setEdit({
                    amount: e.amountCents === null ? '' : (e.amountCents / 100).toFixed(2),
                    note: e.note ?? '',
                    yorkReference: e.yorkReference ?? '',
                  })
                }
              >
                Edit amount, reference or note
              </Button>
            </div>
          )}
          {e.allowedNext.length ? (
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <select className={selectClass} value={next} onChange={(ev) => setNext(ev.target.value)}>
                <option value="">Move to…</option>
                {e.allowedNext.map((s) => (
                  <option key={s} value={s}>
                    {PAYMENT_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
              <input
                className={`${selectClass} flex-1`}
                placeholder="Note (optional)"
                value={note}
                onChange={(ev) => setNote(ev.target.value)}
              />
              <Button size="sm" variant="primary" disabled={!next || move.isLoading} onClick={() => move.mutate()}>
                Change
              </Button>
            </div>
          ) : null}
          {move.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(move.error, 'Could not change the status.')}</ErrorNotice>
            </div>
          ) : null}
          <div className="text-small text-ink-2 mt-6 mb-2">History</div>
          <ul className="text-small space-y-1">
            {(e.history ?? []).map((h, i) => (
              <li key={i} className="border-b border-line pb-1">
                {dateShort(h.at)}: {h.from ? `${PAYMENT_STATUS_LABEL[h.from] ?? h.from} → ` : ''}
                {PAYMENT_STATUS_LABEL[h.to] ?? h.to}
                {h.note ? <div className="text-ink-2">{h.note}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Modal>
  );
}

function Totals({ summary }: { summary: PaymentSummary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-5">
      {PAYMENT_STATUSES.map((s) => {
        const v = summary.byStatus[s];
        const count = typeof v === 'number' ? v : (v?.count ?? 0);
        return (
          <div key={s} className="bg-white border border-line px-4 py-3">
            <div className="text-small text-ink-2">{PAYMENT_STATUS_LABEL[s]}</div>
            <div className="font-semibold text-[22px] mt-1">{fmt(count)}</div>
            {typeof v === 'object' && v ? (
              <div className="text-small text-ink-2">
                {money(v.amountCents)}
                {v.withoutAmount ? ` · ${v.withoutAmount} without amount` : ''}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default function Payments() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTo, setBulkTo] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const stale = params.get('stale') === '1';
  const eventId = params.get('eventId') || undefined;

  const filters: PaymentSummaryQuery = {
    kind: kind || undefined,
    status: status || undefined,
    fiscalYear: fiscalYear || undefined,
    stale: stale ? '1' : undefined,
    eventId,
  };
  const summaryParams: PaymentSummaryQuery = mutable ? filters : { fiscalYear: fiscalYear || undefined };
  const summary = useQuery<PaymentSummary>(
    ['payments-summary', mutable ? filters : { fiscalYear }],
    async () => (await api.get<DataResponse<PaymentSummary>>('/payments/summary', { params: summaryParams })).data.data,
  );
  const listParams: PaymentListQuery = { ...filters, page: String(page) };
  const list = useQuery<PaymentList>(
    ['payments', filters, page],
    async () => (await api.get<DataResponse<PaymentList>>('/payments', { params: listParams })).data.data,
    {
      enabled: mutable,
      keepPreviousData: true,
    },
  );
  const refresh = () => {
    queryClient.invalidateQueries('payments');
    queryClient.invalidateQueries('payments-summary');
  };
  const bulk = useMutation(
    async () =>
      (
        await api.post<DataResponse<PaymentStatusResult>>('/payments/status', {
          ids: [...selected],
          status: bulkTo as PaymentStatusRequest['status'],
          note: bulkNote || undefined,
        } satisfies PaymentStatusRequest)
      ).data.data,
    {
      onSuccess: () => {
        setSelected(new Set());
        refresh();
      },
    },
  );
  const entries = list.data?.entries ?? [];
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.limit)) : 1;
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const resetPage = () => {
    setPage(1);
    setSelected(new Set());
  };

  return (
    <>
      <PageHeader
        title="Payments"
        sub="What DIVERT owes people (stipends, honoraria, gift cards, reimbursements, registrations) and where each payment stands at York. Status only: York finance is the system of record, and the ledger never holds banking details, card numbers, SINs or receipts."
        aside={
          mutable ? (
            <Button variant="primary" className="whitespace-nowrap" onClick={() => setCreating(true)}>
              New entry
            </Button>
          ) : null
        }
      />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {mutable ? (
          <>
            <select
              className={selectClass}
              value={kind}
              onChange={(e) => {
                setKind(e.target.value);
                resetPage();
              }}
            >
              <option value="">Every kind</option>
              {Object.entries(PAYMENT_KINDS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <select
              className={selectClass}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                resetPage();
              }}
            >
              <option value="">Every status</option>
              {PAYMENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {PAYMENT_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </>
        ) : null}
        <select
          className={selectClass}
          value={fiscalYear}
          onChange={(e) => {
            setFiscalYear(e.target.value);
            resetPage();
          }}
        >
          <option value="">Every fiscal year</option>
          {(summary.data?.fiscalYears ?? []).map((fy) => (
            <option key={fy} value={fy}>
              {fy}
            </option>
          ))}
        </select>
        {mutable ? (
          <label className="flex items-center gap-2 text-small">
            <input
              type="checkbox"
              checked={stale}
              onChange={(e) => {
                setParams(e.target.checked ? { stale: '1' } : {});
                resetPage();
              }}
            />
            Needs chasing (owed &gt; 14 days, submitted &gt; 30 days)
          </label>
        ) : null}
        {mutable && eventId ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setParams({});
              resetPage();
            }}
          >
            One event only · show all
          </Button>
        ) : null}
      </div>
      {summary.error ? (
        <ErrorNotice>Could not load the totals.</ErrorNotice>
      ) : summary.data ? (
        <Totals summary={summary.data} />
      ) : (
        <SkeletonRows rows={2} />
      )}
      {!mutable ? (
        <Panel>
          <PanelBody>
            <p className="text-table text-ink-2">
              Amounts and names are financial-status data: admins see the ledger. You see how many entries are at each
              status
              {summary.data?.stale.total ? `, and ${fmt(summary.data.stale.total)} need chasing` : ''}.
            </p>
          </PanelBody>
        </Panel>
      ) : (
        <Panel>
          <PanelHeader title="Ledger" note={list.data ? fmt(list.data.total) : undefined}>
            {selected.size ? (
              <>
                <span className="text-small text-ink-2">{selected.size} selected</span>
                <select className={selectClass} value={bulkTo} onChange={(e) => setBulkTo(e.target.value)}>
                  <option value="">Move to…</option>
                  {PAYMENT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {PAYMENT_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                <input
                  className={selectClass}
                  placeholder="Note (optional)"
                  value={bulkNote}
                  onChange={(e) => setBulkNote(e.target.value)}
                />
                <Button size="sm" variant="primary" disabled={!bulkTo || bulk.isLoading} onClick={() => bulk.mutate()}>
                  Change
                </Button>
              </>
            ) : null}
          </PanelHeader>
          <PanelBody>
            {bulk.error ? (
              <div className="mb-3">
                <ErrorNotice>{errorMessage(bulk.error, 'Could not change the status.')}</ErrorNotice>
              </div>
            ) : null}
            {bulk.data?.refused.length ? (
              <div className="mb-3">
                <ErrorNotice>
                  {`${bulk.data.changed.length} changed; ${bulk.data.refused.length} left as they were: ${[...new Set(bulk.data.refused.map((r) => r.reason))].join('; ')}`}
                </ErrorNotice>
              </div>
            ) : null}
            {list.isLoading ? (
              <SkeletonRows rows={6} />
            ) : list.error ? (
              <ErrorNotice>{errorMessage(list.error, 'Could not load the ledger.')}</ErrorNotice>
            ) : !entries.length ? (
              <EmptyState title="No entries">
                {kind || status || fiscalYear || stale
                  ? 'Nothing matches the filters.'
                  : 'Add the first entry with New entry.'}
              </EmptyState>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                      <th className="py-2 pr-2">
                        <input
                          type="checkbox"
                          checked={entries.every((e) => selected.has(e.id))}
                          onChange={(ev) =>
                            setSelected(ev.target.checked ? new Set(entries.map((e) => e.id)) : new Set())
                          }
                        />
                      </th>
                      <th className="py-2 pr-4">Person</th>
                      <th className="py-2 pr-4">Kind</th>
                      <th className="py-2 pr-4">Activity</th>
                      <th className="py-2 pr-4 text-right">Amount</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Since</th>
                      <th className="py-2 pr-4">FY</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.id} className="border-b border-line text-table align-top">
                        <td className="py-[9px] pr-2">
                          <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
                        </td>
                        <td className="py-[9px] pr-4">
                          <button className="underline text-left" onClick={() => setOpenId(e.id)}>
                            {personName(e.person)}
                          </button>
                          <div className="text-small text-ink-2">
                            {e.person.type === 'contact'
                              ? 'Contact'
                              : e.person.type === 'detached'
                                ? 'Deleted member'
                                : e.person.email}
                          </div>
                        </td>
                        <td className="py-[9px] pr-4">{PAYMENT_KINDS[e.kind] ?? e.kind}</td>
                        <td className="py-[9px] pr-4 text-ink-2">
                          {e.activity.cycle
                            ? `${e.activity.cycle.program ?? ''} ${e.activity.cycle.label}`
                            : e.activity.event
                              ? e.activity.event.title
                              : '—'}
                        </td>
                        <td className="py-[9px] pr-4 text-right tabular-nums">{money(e.amountCents)}</td>
                        <td className="py-[9px] pr-4">
                          <Pill tone={PAYMENT_TONE[e.status]}>{PAYMENT_STATUS_LABEL[e.status]}</Pill>
                        </td>
                        <td className="py-[9px] pr-4 text-ink-2">{dateShort(e.statusChangedAt)}</td>
                        <td className="py-[9px] pr-4 text-ink-2">{e.fiscalYear}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pages > 1 ? (
                  <div className="flex items-center gap-3 mt-4 text-small text-ink-2">
                    <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      Previous
                    </Button>
                    Page {page} of {pages}
                    <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                      Next
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </PanelBody>
        </Panel>
      )}
      {creating ? (
        <NewEntry
          fiscalYear={summary.data && !summary.data.countsOnly ? summary.data.currentFiscalYear : undefined}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}
      {openId ? <EntryDrawer id={openId} onClose={() => setOpenId(null)} onSaved={refresh} /> : null}
    </>
  );
}
